import { inject, Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  IStockImage,
  IStockImageCropSize,
  IStockImageProvider,
  IStockImageSearchResult,
  StockImageAspectRatio,
  StockImageProviderName,
} from '../../../interfaces/stock-image.interface';
import {
  STOCK_IMAGE_CACHE_LIMIT,
  STOCK_IMAGE_CROP_SIZES,
  STOCK_IMAGE_DOWNLOAD_LIMIT,
  STOCK_IMAGE_STRINGS,
  STOCK_IMAGE_TILE_CROP_SIZES,
} from '../../../config/stock-image.constant';
import { PexelsStockImageProvider } from './pexels-stock-image.provider';

const MIME_EXTENSIONS: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
};

/**
 * Entry point for the stock photo library. Which provider is used comes from
 * `environment.stockImage.provider`, so swapping providers is a config change.
 */
@Injectable({ providedIn: 'root' })
export class StockImageService {
  private readonly providers: IStockImageProvider[] = [
    inject(PexelsStockImageProvider),
  ];

  /**
   * Search responses are cached for the life of the tab. Provider free tiers are
   * rate limited per API key (Pexels allows 200 requests an hour shared across
   * every user), so repeat searches and paging back must not re-hit the API.
   */
  private readonly cache = new Map<string, IStockImageSearchResult>();

  /**
   * Downloads in flight or already finished, keyed by photo and ratio, so a
   * download started early by `prefetch` is the same one the confirm waits on.
   */
  private readonly downloads = new Map<string, Observable<File>>();

  get activeProvider(): IStockImageProvider {
    const configured = environment.stockImage
      ?.provider as StockImageProviderName;
    return (
      this.providers.find((provider) => provider.name === configured) ??
      this.providers[0]
    );
  }

  isConfigured(): boolean {
    return this.activeProvider.isConfigured();
  }

  search(
    query: string,
    page: number,
    perPage: number,
    aspectRatio: StockImageAspectRatio = 'original',
  ): Observable<IStockImageSearchResult> {
    const provider = this.activeProvider;
    const trimmed = query.trim();
    // The ratio is part of the key because it can narrow the search itself, so
    // the same term at two ratios is two different result sets.
    const cacheKey = `${provider.name}|${trimmed.toLowerCase()}|${page}|${perPage}|${aspectRatio}`;

    const cached = this.cache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    return provider
      .search(trimmed, page, perPage, aspectRatio)
      .pipe(tap((result) => this.addToCache(cacheKey, result)));
  }

  /**
   * Grid-sized URL, cropped to `aspectRatio` where the provider can do it, so
   * the tile shows the same framing that will end up in the gallery.
   */
  croppedThumbnailUrl(
    image: IStockImage,
    aspectRatio: StockImageAspectRatio,
  ): string {
    return (
      this.cropped(image, STOCK_IMAGE_TILE_CROP_SIZES[aspectRatio]) ??
      image.thumbnailUrl
    );
  }

  /**
   * Preview-sized URL. Where the ratio has a crop it is taken at the download
   * size on purpose - it is then the exact file the upload will fetch, so
   * previewing a photo warms the browser cache for adding it. A ratio with no
   * crop ('cover', which is framed in the image cropper instead) falls back to
   * the provider's own preview size.
   */
  croppedPreviewUrl(
    image: IStockImage,
    aspectRatio: StockImageAspectRatio,
  ): string {
    return (
      this.cropped(image, STOCK_IMAGE_CROP_SIZES[aspectRatio]) ??
      image.previewUrl
    );
  }

  /**
   * Null when there is nothing to crop to - either the caller wants the photo's
   * own ratio, or the active provider cannot crop.
   */
  private cropped(
    image: IStockImage,
    size: IStockImageCropSize | undefined,
  ): string | null {
    if (!size) return null;
    return this.activeProvider.cropUrl?.(image, size) ?? null;
  }

  /**
   * Fetches the chosen photo's bytes so it can go through the normal upload
   * path and become a campaign asset. We never persist the provider URL - the
   * image has to live in our own storage, which provider terms also require.
   */
  downloadAsFile(
    image: IStockImage,
    aspectRatio: StockImageAspectRatio = 'original',
  ): Observable<File> {
    const key = `${image.providerName}|${image.id}|${aspectRatio}`;
    const started = this.downloads.get(key);
    if (started) {
      return started;
    }

    const download = this.fetchAsFile(image, aspectRatio).pipe(
      // A failure must not be kept, or a retry would replay the same error
      // forever. Cleaning up on the shared source rather than on what this
      // call returns means it happens once however the key is reached - the
      // early return above hands back the bare shared observable.
      tap({ error: () => this.downloads.delete(key) }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.rememberDownload(key, download);
    return download;
  }

  /**
   * Starts a photo's download before it is confirmed - a full size photo off
   * the provider's CDN can take seconds, and doing it while the user is still
   * looking at the grid takes that wait off the Add button.
   */
  prefetch(
    image: IStockImage,
    aspectRatio: StockImageAspectRatio = 'original',
  ): void {
    this.downloadAsFile(image, aspectRatio).subscribe({
      // Nothing to report: this is speculative, and confirming asks again.
      error: () => undefined,
    });
  }

  /**
   * Drops the downloaded photos. They are whole image files, and they are only
   * worth keeping for the moment between selecting a photo and confirming it,
   * so the picker calls this when it closes. Search results are deliberately
   * left alone: those are what keeps repeat searches off the provider's hourly
   * request limit, and they are only metadata.
   */
  releaseDownloads(): void {
    this.downloads.clear();
  }

  private rememberDownload(key: string, download: Observable<File>): void {
    if (this.downloads.size >= STOCK_IMAGE_DOWNLOAD_LIMIT) {
      const oldestKey = this.downloads.keys().next().value;
      if (oldestKey) {
        this.downloads.delete(oldestKey);
      }
    }
    this.downloads.set(key, download);
  }

  private fetchAsFile(
    image: IStockImage,
    aspectRatio: StockImageAspectRatio,
  ): Observable<File> {
    const downloadUrl =
      this.cropped(image, STOCK_IMAGE_CROP_SIZES[aspectRatio]) ??
      image.downloadUrl;

    return from(fetch(downloadUrl)).pipe(
      switchMap((response: Response) => {
        if (!response.ok) {
          return throwError(
            () => new Error(STOCK_IMAGE_STRINGS.DOWNLOAD_FAILED),
          );
        }
        return from(response.blob());
      }),
      map((blob: Blob) => {
        const contentType = blob.type || 'image/jpeg';
        return new File([blob], this.fileNameFor(image, contentType), {
          type: contentType,
        });
      }),
    );
  }

  private fileNameFor(image: IStockImage, contentType: string): string {
    const extension = MIME_EXTENSIONS[contentType.toLowerCase()] ?? '.jpg';
    return `${image.providerName}-${image.id}${extension}`;
  }

  private addToCache(key: string, result: IStockImageSearchResult): void {
    if (this.cache.size >= STOCK_IMAGE_CACHE_LIMIT) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(key, result);
  }
}
